import React, { useState, useMemo, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Bot, Users, BarChart3, AlertTriangle, Key, CreditCard, Package, Undo2 } from 'lucide-react';
import './style.css';

interface SidebarProps {
  isOpen: boolean;
}

interface SidebarItem {
  title: string;
  to: string;
  icon: React.ReactNode;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(true);

  const sidebarItems: SidebarItem[] = useMemo(() => [
    {
      title: 'Users',
      to: '/app/users',
      icon: <Users size={16} />
    },
    {
      title: 'Agent Analytics',
      to: '/app/agents/analytics',
      icon: <BarChart3 size={16} />
    },
    {
      title: 'Error Logs',
      to: '/app/error-logs',
      icon: <AlertTriangle size={16} />
    },
    {
      title: 'VAPI Keys',
      to: '/app/vapi-keys',
      icon: <Key size={16} />
    },
    {
      title: 'Plans & Pricing',
      to: '/app/subscriptions/plans',
      icon: <CreditCard size={16} />
    },
    {
      title: 'Optional Add-ons',
      to: '/app/subscriptions/addons',
      icon: <Package size={16} />
    },
    {
      title: 'User Subscriptions',
      to: '/app/subscriptions/users',
      icon: <CreditCard size={16} />
    },
    {
      title: 'Refund Requests',
      to: '/app/subscriptions/refund-requests',
      icon: <Undo2 size={16} />
    },
  ], []);

  const toggleMenu = useCallback((): void => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const isActiveRoute = useCallback((path: string): boolean => {
    // For exact matches
    if (location.pathname === path) {
      return true;
    }

    // For nested routes, we need to be more specific
    // Check if current path starts with the menu path but isn't a longer nested route
    const pathSegments = path.split('/').filter(Boolean);
    const currentSegments = location.pathname.split('/').filter(Boolean);

    // If the menu path has fewer segments than current path
    if (pathSegments.length < currentSegments.length) {
      // Check if all menu path segments match the beginning of current path
      const isParentMatch = pathSegments.every((segment, index) =>
        currentSegments[index] === segment
      );

      // Only return true if there's no more specific menu item that would match
      if (isParentMatch) {
        // Check if there's a more specific menu item that should be active instead
        const hasMoreSpecificMatch = sidebarItems.some(item => {
          const itemSegments = item.to.split('/').filter(Boolean);
          return itemSegments.length > pathSegments.length &&
            itemSegments.every((segment, index) => currentSegments[index] === segment) &&
            location.pathname.startsWith(item.to);
        });

        return !hasMoreSpecificMatch;
      }
    }

    return false;
  }, [location.pathname, sidebarItems]);

  return (
    <div className={`main-sidebar-div ${isOpen ? 'show' : ''} relative h-full bg-gradient-to-b from-white to-yellow-50 border-r border-gray-200 shadow-sm transition-all duration-300 ease-in-out`}>
      <div className="outer-wrapper h-full">
        <div
          className={`inner-wrapper flex flex-col h-full transition-all duration-300 ${isMenuOpen ? 'w-64' : 'w-20'}`}
          style={{
            width: isMenuOpen ? 'fit-content' : '50px',
          }}
        >
          <div
            className="toggle-icon-div absolute -right-3 top-6 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white border border-yellow-200 text-gray-600 shadow-md transition-all duration-200 hover:bg-yellow-500 hover:text-white hover:scale-110 hover:border-transparent cursor-pointer"
            title={isMenuOpen ? 'Close' : 'Show'}
          >
            {isMenuOpen ? (
              <ChevronLeft
                className="menu-icon h-4 w-4"
                onClick={toggleMenu}
              />
            ) : (
              <ChevronRight
                className="menu-icon h-4 w-4"
                onClick={toggleMenu}
              />
            )}
          </div>

          <div className="listItem mt-16 flex-1 px-3">
            <ul className="space-y-1">
              <div
                className="li-main-div"
                style={{
                  padding: isMenuOpen ? '0' : '0',
                }}
              >
                {sidebarItems.map((item) => {
                  const isActive = isActiveRoute(item.to);

                  return (
                    <li
                      key={item.to}
                      className={`${isActive ? 'active' : ''} group`}
                    >
                      <NavLink
                        to={item.to}
                        className={`relative flex items-center px-3 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 group ${isActive
                            ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-md'
                            : 'text-gray-700 hover:bg-yellow-50 hover:text-gray-900'
                          } ${!isMenuOpen ? 'justify-center' : ''}`}
                        style={{
                          justifyContent: isMenuOpen ? '' : 'center',
                        }}
                      >
                        {isActive && isMenuOpen && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full -ml-3" />
                        )}

                        <span className={`flex items-center justify-center transition-transform duration-200 ${!isActive && 'group-hover:scale-110'} ${!isMenuOpen ? 'mx-auto' : 'mr-3'}`}>
                          {item.icon}
                        </span>

                        <p
                          className={`truncate transition-opacity duration-200 ${isMenuOpen ? 'block' : 'hidden'}`}
                          style={{
                            display: isMenuOpen ? 'block' : 'none',
                          }}
                        >
                          {item.title}
                        </p>

                        {!isMenuOpen && (
                          <div className="absolute left-full ml-6 px-2 py-1 bg-gray-800 text-white text-xs rounded-md opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
                            {item.title}
                            <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-gray-800 rotate-45" />
                          </div>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </div>
            </ul>
          </div>

          <div className="sidebar-footer px-3 pb-4 mt-3 border-t border-gray-200 pt-3">
            <div className="footer-ul">
              <ul>
                <li>
                  {/* Footer items can be added here */}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;